import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stage, Layer, Rect, Text, Group, Transformer } from 'react-konva';
import type { Section, Lot, LotStatus } from '../../types/inventory';
import { STATUS_CONFIG } from '../../types/inventory';

interface MapCanvasProps {
  sections: Section[];
  lots: Lot[];
  selectedLotId?: string | null;
  onLotClick: (lot: Lot) => void;
  onSectionClick?: (section: Section) => void;
  onSectionDragEnd?: (sectionId: string, x: number, y: number) => void;
  filterStatus?: LotStatus | '';
  filterSection?: string;
  showLabels?: boolean;
  placementMode?: boolean;
  onPlaceLot?: (sectionId: string, x: number, y: number) => void;
  editMode?: boolean;
  onLotDragEnd?: (lotId: string, x: number, y: number) => void;
  onLotResize?: (lotId: string, x: number, y: number, width: number, height: number) => void;
}

// Shared padding constants for section layout
const SECTION_PAD_X = 24;
const SECTION_PAD_TOP = 40;
const SECTION_PAD_BOTTOM = 16;

const calcSectionW = (cols: number, lotW: number, gap: number) =>
  SECTION_PAD_X * 2 + cols * lotW + Math.max(0, cols - 1) * gap;
const calcSectionH = (rows: number, lotH: number, gap: number) =>
  SECTION_PAD_TOP + SECTION_PAD_BOTTOM + rows * lotH + Math.max(0, rows - 1) * gap;

const MapCanvas: React.FC<MapCanvasProps> = ({
  sections, lots, selectedLotId, onLotClick, onSectionClick, onSectionDragEnd,
  filterStatus, filterSection, showLabels = true,
  placementMode = false, onPlaceLot,
  editMode = false, onLotDragEnd, onLotResize,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 800, h: 600 });
  const [scale, setScale] = useState(1);
  const [hoveredLotId, setHoveredLotId] = useState<string | null>(null);
  const [draggingLotId, setDraggingLotId] = useState<string | null>(null);
  const [editSelectedId, setEditSelectedId] = useState<string | null>(null);
  const trRef = useRef<any>(null);
  const lotNodeRefs = useRef<Map<string, any>>(new Map());

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setDims({ w: containerRef.current.offsetWidth, h: containerRef.current.offsetHeight });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault();
    const scaleBy = 1.08;
    const stage = e.target.getStage();
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const mousePointTo = { x: (pointer.x - stage.x()) / oldScale, y: (pointer.y - stage.y()) / oldScale };
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const clamped = Math.max(0.3, Math.min(3, newScale));
    setScale(clamped);
    stage.scale({ x: clamped, y: clamped });
    const newPos = { x: pointer.x - mousePointTo.x * clamped, y: pointer.y - mousePointTo.y * clamped };
    stage.position(newPos);
    stage.batchDraw();
  }, []);

  // Clear transformer selection when leaving edit mode
  useEffect(() => { if (!editMode) setEditSelectedId(null); }, [editMode]);

  // Attach transformer to selected lot
  useEffect(() => {
    if (!trRef.current) return;
    if (editMode && editSelectedId) {
      const node = lotNodeRefs.current.get(editSelectedId);
      if (node) {
        trRef.current.nodes([node]);
        trRef.current.getLayer()?.batchDraw();
      }
    } else {
      trRef.current.nodes([]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [editMode, editSelectedId]);

  const getStatusColor = (status: LotStatus, alpha: number = 1) => {
    const c = STATUS_CONFIG[status]?.color || '#64748B';
    if (alpha === 1) return c;
    const r = parseInt(c.slice(1, 3), 16);
    const g = parseInt(c.slice(3, 5), 16);
    const b = parseInt(c.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const isFiltered = (lot: Lot) => {
    if (filterStatus && lot.status !== filterStatus) return false;
    if (filterSection && lot.sectionId !== filterSection) return false;
    return true;
  };

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'hidden', background: 'var(--bg-surface)', position: 'relative' }}>
      {/* @ts-ignore */}
      <Stage width={dims.w} height={dims.h} draggable onWheel={handleWheel}
        onMouseDown={(e: any) => { if (editMode && e.target === e.target.getStage()) setEditSelectedId(null); }}
        onTouchStart={(e: any) => { if (editMode && e.target === e.target.getStage()) setEditSelectedId(null); }}
      >
        <Layer>
          {/* Grid dots background */}
          {Array.from({ length: 60 }).map((_, i) =>
            Array.from({ length: 40 }).map((_, j) => (
              <Rect key={`g${i}-${j}`} x={i * 40} y={j * 40} width={1} height={1} fill="rgba(148,163,184,0.06)" />
            ))
          )}

          {/* Sections with nested lots */}
          {sections.filter(s => s.isActive).map(sec => {
            const isActiveSection = !filterSection || filterSection === sec.id;
            const secLots = lots.filter(l => l.sectionId === sec.id);

            // Dynamically calculate section dimensions from grid config
            let renderW: number, renderH: number;
            if (sec.layoutMode === 'FREEFORM') {
              const fLots = secLots.filter(l => l.isFreeform);
              if (fLots.length > 0) {
                const maxR = Math.max(...fLots.map(l => (l.x_coord - sec.x) + (l.width || sec.lotWidth)));
                const maxB = Math.max(...fLots.map(l => (l.y_coord - sec.y) + (l.height || sec.lotHeight)));
                renderW = Math.max(sec.width, maxR + SECTION_PAD_X);
                renderH = Math.max(sec.height, maxB + SECTION_PAD_BOTTOM);
              } else {
                renderW = sec.width;
                renderH = sec.height;
              }
            } else {
              const gridW = calcSectionW(sec.cols, sec.lotWidth, sec.lotGap);
              const gridH = calcSectionH(sec.rows, sec.lotHeight, sec.lotGap);
              if (sec.layoutMode === 'HYBRID') {
                const fLots = secLots.filter(l => l.isFreeform);
                if (fLots.length > 0) {
                  renderW = Math.max(gridW, ...fLots.map(l => (l.x_coord - sec.x) + (l.width || sec.lotWidth) + SECTION_PAD_X));
                  renderH = Math.max(gridH, ...fLots.map(l => (l.y_coord - sec.y) + (l.height || sec.lotHeight) + SECTION_PAD_BOTTOM));
                } else {
                  renderW = gridW;
                  renderH = gridH;
                }
              } else {
                renderW = gridW;
                renderH = gridH;
              }
            }

            const sectionDraggable = !placementMode && !editMode;

            return (
              <Group
                key={sec.id}
                x={sec.x}
                y={sec.y}
                opacity={isActiveSection ? 1 : 0.3}
                draggable={sectionDraggable}
                onDragEnd={(e: any) => {
                  const node = e.target;
                  onSectionDragEnd?.(sec.id, Math.round(node.x()), Math.round(node.y()));
                }}
              >
                {/* Section background */}
                <Rect
                  width={renderW} height={renderH}
                  fill="rgba(148,163,184,0.15)"
                  stroke={sec.color || 'rgba(148,163,184,0.25)'}
                  strokeWidth={1.5}
                  cornerRadius={8}
                  onClick={(e: any) => {
                    if (placementMode && onPlaceLot && (sec.layoutMode === 'FREEFORM' || sec.layoutMode === 'HYBRID')) {
                      const stage = e.target.getStage();
                      const pointer = stage.getPointerPosition();
                      const stageScale = stage.scaleX();
                      const stagePos = stage.position();
                      const absX = (pointer.x - stagePos.x) / stageScale;
                      const absY = (pointer.y - stagePos.y) / stageScale;
                      onPlaceLot(sec.id, Math.round(absX), Math.round(absY));
                    }
                  }}
                />
                {/* Section label */}
                {showLabels && (
                  <>
                    <Text text={sec.name} x={8} y={6} fontSize={14} fontStyle="bold" fill={sec.color || '#94A3B8'} opacity={0.9} />
                    <Text text={sec.label} x={28} y={8} fontSize={10} fill="rgba(148,163,184,0.50)" />
                  </>
                )}

                {/* Lots inside section (relative coordinates) */}
                {secLots.map(lot => {
                  const visible = isFiltered(lot);
                  const isSelected = lot.id === selectedLotId;
                  const isHovered = lot.id === hoveredLotId;
                  const isDragging = lot.id === draggingLotId;
                  const lw = lot.width || sec.lotWidth;
                  const lh = lot.height || sec.lotHeight;
                  const canDrag = editMode && (lot.isFreeform || sec.layoutMode === 'FREEFORM' || sec.layoutMode === 'HYBRID');

                  // Calculate lot position RELATIVE to section origin
                  const relX = (!lot.isFreeform && lot.row !== undefined && lot.col !== undefined)
                    ? SECTION_PAD_X + lot.col * (sec.lotWidth + sec.lotGap)
                    : lot.x_coord - sec.x;
                  const relY = (!lot.isFreeform && lot.row !== undefined && lot.col !== undefined)
                    ? SECTION_PAD_TOP + lot.row * (sec.lotHeight + sec.lotGap)
                    : lot.y_coord - sec.y;

                  return (
                    <Group
                      key={lot.id}
                      ref={(node: any) => { if (node) lotNodeRefs.current.set(lot.id, node); else lotNodeRefs.current.delete(lot.id); }}
                      x={relX}
                      y={relY}
                      opacity={visible ? (isDragging ? 0.85 : 1) : 0.15}
                      draggable={canDrag && visible}
                      onClick={(e: any) => {
                        e.cancelBubble = true;
                        if (editMode && canDrag && visible) { setEditSelectedId(lot.id); }
                        else if (!isDragging && visible) { onLotClick(lot); }
                      }}
                      onTap={(e: any) => {
                        e.cancelBubble = true;
                        if (editMode && canDrag && visible) { setEditSelectedId(lot.id); }
                        else if (!isDragging && visible) { onLotClick(lot); }
                      }}
                      onTransformEnd={(e: any) => {
                        const node = e.target;
                        const sx = node.scaleX();
                        const sy = node.scaleY();
                        node.scaleX(1);
                        node.scaleY(1);
                        const absX = Math.round(node.x()) + sec.x;
                        const absY = Math.round(node.y()) + sec.y;
                        onLotResize?.(lot.id, absX, absY, Math.max(20, Math.round(lw * sx)), Math.max(20, Math.round(lh * sy)));
                      }}
                      onMouseEnter={(e: any) => {
                        setHoveredLotId(lot.id);
                        e.target.getStage()!.container().style.cursor = canDrag && visible ? 'grab' : 'pointer';
                      }}
                      onMouseLeave={(e: any) => {
                        setHoveredLotId(null);
                        e.target.getStage()!.container().style.cursor = sectionDraggable ? 'grab' : 'default';
                      }}
                      onDragStart={(e: any) => {
                        e.cancelBubble = true;
                        if (!canDrag) return;
                        setDraggingLotId(lot.id);
                        e.target.getStage()!.container().style.cursor = 'grabbing';
                      }}
                      onDragEnd={(e: any) => {
                        e.cancelBubble = true;
                        if (!canDrag) return;
                        setDraggingLotId(null);
                        e.target.getStage()!.container().style.cursor = 'grab';
                        const node = e.target;
                        const absX = Math.round(node.x()) + sec.x;
                        const absY = Math.round(node.y()) + sec.y;
                        onLotDragEnd?.(lot.id, absX, absY);
                      }}
                    >
                      {isDragging && (
                        <Rect width={lw + 4} height={lh + 4} x={-2} y={-2} fill="transparent" stroke="#0D9488" strokeWidth={2} cornerRadius={lot.isFreeform ? 6 : 4} dash={[6, 3]} />
                      )}
                      <Rect
                        width={lw} height={lh}
                        fill={getStatusColor(lot.status, isDragging ? 0.95 : isHovered ? 0.9 : 0.7)}
                        stroke={isDragging ? '#0D9488' : isSelected ? '#FFFFFF' : isHovered ? 'rgba(255,255,255,0.5)' : lot.isFreeform ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.20)'}
                        strokeWidth={isDragging ? 2 : isSelected ? 2 : lot.isFreeform ? 1.5 : 1}
                        dash={lot.isFreeform && !isSelected && !isDragging ? [4, 2] : undefined}
                        cornerRadius={lot.isFreeform ? 5 : 3}
                        shadowBlur={isDragging ? 12 : isSelected ? 8 : isHovered ? 4 : 0}
                        shadowColor={isDragging ? '#0D9488' : isSelected ? '#0D9488' : 'rgba(0,0,0,0.3)'}
                        shadowOpacity={isDragging ? 0.8 : 0.6}
                      />
                      <Text
                        text={lot.lotNumber}
                        width={lw} height={lh}
                        align="center" verticalAlign="middle"
                        fontSize={lw < 36 ? 8 : 10}
                        fontStyle="600"
                        fill={isSelected || isHovered || isDragging ? '#FFFFFF' : 'rgba(255,255,255,0.85)'}
                      />
                      {canDrag && visible && !isDragging && (
                        <Text text="⤡" x={lw - 11} y={1} fontSize={9} fill="rgba(13,148,136,0.7)" />
                      )}
                      {lot.isLocked && !canDrag && (
                        <Text text="🔒" x={lw - 12} y={2} fontSize={8} />
                      )}
                    </Group>
                  );
                })}
              </Group>
            );
          })}

          {/* Transformer for resize in edit mode */}
          {editMode && (
            <Transformer
              ref={trRef}
              rotateEnabled={false}
              keepRatio={false}
              enabledAnchors={['top-left','top-right','bottom-left','bottom-right','middle-left','middle-right','top-center','bottom-center']}
              boundBoxFunc={(_oldBox: any, newBox: any) => {
                if (newBox.width < 20 || newBox.height < 20) return _oldBox;
                return newBox;
              }}
              borderStroke="#0D9488"
              borderStrokeWidth={1.5}
              anchorFill="#0D9488"
              anchorStroke="#FFFFFF"
              anchorSize={8}
              anchorCornerRadius={2}
            />
          )}
        </Layer>
      </Stage>

      {/* Zoom indicator */}
      <div style={{
        position: 'absolute', bottom: '12px', right: '12px',
        background: 'var(--modal-bg)', backdropFilter: 'blur(8px)',
        borderRadius: '6px', padding: '4px 10px',
        fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
        border: '1px solid var(--border-base)',
      }}>
        {Math.round(scale * 100)}%
      </div>
    </div>
  );
};

export default MapCanvas;
